import { useEffect, useState } from "react";
import api from "../logic/axios";
import {
  Box,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Text,
  Select
} from "@chakra-ui/react";
import { DeleteIcon, AddIcon, RepeatIcon } from "@chakra-ui/icons"; 

export const Dashboard = ({ onLogout }) => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  const rewardTypeMapping = {
    1: "Currency",
    2: "Unit",
    3: "LuckySpin",
    4: "GameEvent",
    5: "CharacterSkin",
  };

  // для модального окна
  const { isOpen, onOpen, onClose } = useDisclosure();

  // данные новой записи
  const [newPromo, setNewPromo] = useState({
    code: "",
    description: "",
    rewards: [
      { type: 1, amount: null, itemId: 0 },
    ],
    maxActivations: "",
    expiresAt: "",
  });

  useEffect(() => {
    fetchPromos();
  }, []);

  const fetchPromos = () => {
    setLoading(true);
    api
      .get("promos/all")
      .then((res) => {
        setPromos(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Ошибка загрузки промокодов:", err);
        setLoading(false);
      });
  };

  const handleDelete = async (id) => {
    try {
      if (!window.confirm("Удалить этот промокод?")) return;

      await api.delete(`promos/${id}`);
      setPromos(promos.filter((p) => p.id !== id));
    } catch (err) {
      console.error("Ошибка удаления:", err);
    }
  };

 const handleCreate = async () => {
  try {
      let expiresAt = null;

      if (newPromo.expiresAt) {
        // datetime-local → UTC
        const localDate = new Date(newPromo.expiresAt);
        expiresAt = localDate.toISOString(); // формат: 2024-09-22T20:30:00.000Z
      }

      await api.post("promos/create", {
        code: newPromo.code,
        description: newPromo.description,
        rewardType: newPromo.rewards[0].type,
        reward: newPromo.rewards[0].itemId,
        maxActivations: newPromo.maxActivations
          ? parseInt(newPromo.maxActivations)
          : null,
        expiresAt: expiresAt,
      });

      onClose();
      setNewPromo({
        code: "",
        description: "",
        rewards: [
          { type: 1, amount: null, itemId: 0 },
        ],
        maxActivations: "",
        expiresAt: "",
      });
      fetchPromos();
      setError("");

    } catch (err) {
      setError(err["response"]["data"]);
    }
  };

  return (
    <Box className="relative min-h-screen" bg="gray.900" color="gray.100" p={4}>
      {/* Заголовок */}
      <Box className="flex justify-between items-center mb-6">
        <Heading size="lg" color="teal.300">
          🎟️ Список промокодов
        </Heading>
        <Box className="flex gap-3">
          <Button 
            leftIcon={<RepeatIcon />} 
            colorScheme="blue" 
            onClick={fetchPromos}
            isLoading={loading}
          >
            Обновить
          </Button>
          <Button 
            leftIcon={<AddIcon />} 
            colorScheme="green" 
            onClick={onOpen}
          >
            Добавить промокод
          </Button>
        </Box>
      </Box>

      {/* Контент */}
      <Box
        bg="gray.800"
        p={6}
        rounded="2xl"
        shadow="xl"
        w="100%"
        h="full"
        overflow="auto"
      >
        {loading ? (
          <Box className="flex justify-center items-center h-full">
            <Spinner size="xl" color="teal.300" />
          </Box>
        ) : (
          <Table variant="simple" size="md" colorScheme="teal">
            <Thead>
              <Tr>
                <Th color="teal.200">Id</Th>
                <Th color="teal.200">Code</Th>
                <Th color="teal.200">Description</Th>
                <Th color="teal.200">Reward Type</Th>
                <Th color="teal.200">Reward</Th>
                <Th color="teal.200">Activations</Th>
                <Th color="teal.200">Max</Th>
                <Th color="teal.200">Status</Th>
                <Th color="teal.200">Expires At</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {promos.map((promo) => (
                <Tr key={promo.id} _hover={{ bg: "gray.700" }}>
                  <Td>{promo.id}</Td>
                  <Td>{promo.code}</Td>
                  <Td>{promo.description}</Td>
                  <Td>
                    {promo.rewards.length > 0 ? (
                      <ul>
                        {promo.rewards.map(r => (
                          <li key={r.id}>{rewardTypeMapping[r.type] ?? r.type}</li>
                        ))}
                      </ul>
                    ) : (
                      "—"
                    )}
                  </Td>
                  <Td>
                    {promo.rewards.length > 0 ? (
                      <ul>
                        {promo.rewards.map(r => (
                          <li key={r.id}>{r.itemId ?? r.type}</li>
                        ))}
                      </ul>
                    ) : (
                      "—"
                    )}
                  </Td>
                  <Td>{promo.currentActivations}</Td>
                  <Td>{promo.maxActivations ?? "∞"}</Td>
                  <Td color={promo.isActive ? "green.300" : "red.300"}>
                    {promo.isActive ? "Active" : "Inactive"}
                  </Td>
                  <Td>
                    {promo.expiresAt
                      ? new Date(promo.expiresAt).toLocaleString()
                      : "—"}
                  </Td>
                  <Td>
                    <Button
                      size="sm"
                      colorScheme="red"
                      onClick={() => handleDelete(promo.id)}
                    >
                      <DeleteIcon />
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      {/* Кнопка Logout */}
      <Button
        colorScheme="red"
        position="absolute"
        bottom="20px"
        left="20px"
        onClick={onLogout}
      >
        Logout
      </Button>

      {/* Модальное окно */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent bg="gray.800" color="gray.100">
          <ModalHeader>Создание промокода</ModalHeader>
          <ModalCloseButton />
          <ModalBody>

            <FormControl mb={3}>
              <FormLabel>Code</FormLabel>
              <Input
                value={newPromo.code}
                onChange={(e) =>
                  setNewPromo({ ...newPromo, code: e.target.value })
                }
              />
            </FormControl>

            <FormControl mb={3}>
              <FormLabel>Description</FormLabel>
              <Input
                value={newPromo.description}
                onChange={(e) =>
                  setNewPromo({ ...newPromo, description: e.target.value })
                }
              />
            </FormControl>

            <FormControl mb={3}>
              <FormLabel>Reward Type</FormLabel>
              <Select
                value={newPromo.rewards[0].type}
                onChange={(e) =>
                  setNewPromo({
                    ...newPromo,
                    rewards: [
                      { ...newPromo.rewards[0], type: parseInt(e.target.value, 10) },
                    ],
                  })
                }
              >
                {Object.entries(rewardTypeMapping).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </FormControl>

            {/* если type === 1 */}
            {newPromo.rewards[0].type === 1 && (
              <FormControl mb={3}>
                <FormLabel>Currency</FormLabel>
                <Input
                  value={newPromo.rewards[0].itemId || ""}
                  onChange={(e) =>
                    setNewPromo({
                      ...newPromo,
                      rewards: [{ ...newPromo.rewards[0], itemId: e.target.value }],
                    })
                  }
                />
              </FormControl>
            )}

            {/* если type === 2 */}
            {newPromo.rewards[0].type === 2 && (
              <FormControl mb={3}>
                <FormLabel>Unit Id</FormLabel>
                <Input
                  value={newPromo.rewards[0].itemId || ""}
                  onChange={(e) =>
                    setNewPromo({
                      ...newPromo,
                      rewards: [{ ...newPromo.rewards[0], itemId: e.target.value }],
                    })
                  }
                />
              </FormControl>
            )}

            {/* если type === 3 */}
            {newPromo.rewards[0].type === 3 && (
              <FormControl mb={3}>
                <FormLabel>Lucky Spin</FormLabel>
                <Input
                  value={newPromo.rewards[0].itemId || ""}
                  onChange={(e) =>
                    setNewPromo({
                      ...newPromo,
                      rewards: [{ ...newPromo.rewards[0], itemId: e.target.value }],
                    })
                  }
                />
              </FormControl>
            )}

            {/* если type === 4 */}
            {newPromo.rewards[0].type === 4 && (
              <FormControl mb={3}>
                <FormLabel>Game Event Id</FormLabel>
                <Input
                  value={newPromo.rewards[0].itemId || ""}
                  onChange={(e) =>
                    setNewPromo({
                      ...newPromo,
                      rewards: [{ ...newPromo.rewards[0], itemId: e.target.value }],
                    })
                  }
                />
              </FormControl>
            )}

            {/* если type === 5 */}
            {newPromo.rewards[0].type === 5 && (
              <FormControl mb={3}>
                <FormLabel>Character Skin Id</FormLabel>
                <Input
                  value={newPromo.rewards[0].itemId || ""}
                  onChange={(e) =>
                    setNewPromo({
                      ...newPromo,
                      rewards: [{ ...newPromo.rewards[0], itemId: e.target.value }],
                    })
                  }
                />
              </FormControl>
            )}

            <FormControl mb={3}>
              <FormLabel>Max Activations</FormLabel>
              <Input
                type="number"
                value={newPromo.maxActivations}
                onChange={(e) =>
                  setNewPromo({ ...newPromo, maxActivations: e.target.value })
                }
              />
            </FormControl>

            <FormControl mb={3}>
              <FormLabel>Expires At</FormLabel>
              <Input
                type="datetime-local"
                value={newPromo.expiresAt}
                onChange={(e) =>
                  setNewPromo({ ...newPromo, expiresAt: e.target.value })
                }
              />
            </FormControl>
          </ModalBody>

          {error && (
                        <Text color="red.400" fontSize="sm" textAlign="center">
                          {error}
                        </Text>
                      )}

          <ModalFooter>
            <Button colorScheme="green" mr={3} onClick={handleCreate}>
              Создать
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Отмена
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
